/**
 * Sliders Module
 * All Splide slider initializations
 */

import { Splide } from "@splidejs/splide";
import { animateHeroContent, animateThumbnail } from "./animations.js";

export function initSliders() {
  // console.log("Initializing sliders...");

  initTopbarRibbonSlider();
  initHeroSlider();
  initProductSliders();
  initTestimonialSlider();
  initProductReviewSlider();
  initFeaturedPostSlider();
  // initInstagramFeedSlider();
}

/**
 * Instagram feed di `#sbi_images`: dibungkus jadi Splide murni lewat JS, jadi
 * struktur HTML di template tidak diubah. PerPage: 5 desktop, 4 tablet,
 * 1.5 mobile. Arrows hanya muncul jika jumlah item berlebih vs perPage aktif,
 * sehingga 5 item (placeholder) di desktop tetap tampak statis, tapi di
 * tablet/mobile tetap bisa digeser.
 */
// function initInstagramFeedSlider() {
//   const target = document.querySelector("#sbi_images");
//   if (!target) return;

//   const items = Array.from(target.querySelectorAll(".sbi_item"));
//   if (items.length < 2) return;

//   const moreThan = (count) => items.length > count;

//   const track = document.createElement("div");
//   track.className = "splide__track";
//   const list = document.createElement("ul");
//   list.className = "splide__list";

//   items.forEach((el) => {
//     el.classList.add("splide__slide");
//     list.appendChild(el);
//   });

//   track.appendChild(list);
//   target.appendChild(track);

//   // Wajib: Splide mencari track lewat find() yang memvalidasi
//   // `closest(track, '.splide') === root`. Tanpa class `splide` di root, track
//   // dianggap milik slider lain dan mount() melempar "A track/list element is
//   // missing." — error itu menghentikan sisa init() di main.js. Slider lain
//   // sudah punya class ini dari markup PHP-nya.
//   target.classList.add("splide");

//   new Splide(target, {
//     type: "slide",
//     perMove: 1,
//     perPage: 5,
//     gap: "1rem",
//     arrows: moreThan(5),
//     pagination: false,
//     breakpoints: {
//       991: {
//         perPage: 4,
//         arrows: moreThan(4),
//       },
//       767: {
//         perPage: 1.5,
//         arrows: moreThan(1.5),
//       },
//     },
//   }).mount();
// }

function initTopbarRibbonSlider() {
  const sliders = document.querySelectorAll(".topbarRibbonSlider");
  if (!sliders.length) return;

  sliders.forEach((element) => {
    const slideCount = element.querySelectorAll(".splide__slide").length;
    const isLoop = slideCount > 1;

    new Splide(element, {
      type: "fade",
      perPage: 1,
      arrows: isLoop,
      pagination: false,
      autoplay: isLoop,
      interval: 5000,
      speed: 600,
      rewind: true,
    }).mount();
  });
}

function initHeroSlider() {
  const sliders = document.querySelectorAll(".splideHeroSlider");
  if (!sliders.length) return;

  sliders.forEach((element) => {
    const autoplaySpeed = 5000;
    const slideCount = element.querySelectorAll(".splide__slide").length;
    const isLoop = slideCount > 1;

    const splide = new Splide(element, {
      autoplay: isLoop,
      type: isLoop ? "loop" : "slide",
      perPage: 1,
      arrows: isLoop,
      pagination: isLoop,
      interval: autoplaySpeed,
      speed: 800,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      pauseOnHover: false,
    });

    const progressBar = element.querySelector(".carousel-progress__bar");
    let progressAnimation = null;
    let startTime = null;

    const animateProgress = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min((elapsed / autoplaySpeed) * 100, 100);

      if (progressBar) {
        progressBar.style.width = `${progress}%`;
      }

      if (progress < 100) {
        progressAnimation = requestAnimationFrame(animateProgress);
      }
    };

    const startProgress = () => {
      if (progressAnimation) {
        cancelAnimationFrame(progressAnimation);
      }
      startTime = null;
      if (progressBar) {
        progressBar.style.width = "0%";
      }
      if (isLoop) {
        progressAnimation = requestAnimationFrame(animateProgress);
      }
    };

    const stopProgress = () => {
      if (progressAnimation) {
        cancelAnimationFrame(progressAnimation);
      }
    };

    splide.on("mounted", () => {
      startProgress();
      const activeSlide = element.querySelector(".splide__slide.is-active .hero__content");
      animateHeroContent(activeSlide);
    });

    splide.on("move", () => {
      startProgress();
      const activeSlide = element.querySelector(".splide__slide.is-active .hero__content");
      animateHeroContent(activeSlide);
    });

    splide.on("autoplay:play", () => {
      startProgress();
    });

    splide.on("autoplay:pause", () => {
      stopProgress();
    });

    splide.mount();
  });
}

function initProductSliders() {
  const sliders = document.querySelectorAll(".splide-product-slider");
  if (!sliders.length) return;

  sliders.forEach((element) => {
    new Splide(element, {
      type: "slide",
      autoWidth: true,
      perMove: 1,
      gap: "1rem",
      arrows: true,
      pagination: false,
    }).mount();
  });
}

function initFadeAutoplaySlider(selector) {
  const sliders = document.querySelectorAll(selector);
  if (!sliders.length) return;

  sliders.forEach((element) => {
    const slideCount = element.querySelectorAll(".splide__slide").length;
    const isLoop = slideCount > 1;

    new Splide(element, {
      type: "fade",
      rewind: true,
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      autoplay: isLoop,
      interval: 5000,
      speed: 800,
      pauseOnHover: false,
    }).mount();
  });
}

/**
 * Testimonial: 4 kartu per view di desktop, 2 di tablet, 1 di mobile — bukan
 * type "fade" (cuma bisa 1 slide per view), jadi pakai konfigurasi sendiri.
 */
function initTestimonialSlider() {
  const sliders = document.querySelectorAll(".splideTestimonialSlider");
  if (!sliders.length) return;

  sliders.forEach((element) => {
    const slideCount = element.querySelectorAll(".splide__slide").length;
    const isLoop = slideCount > 1;

    new Splide(element, {
      type: isLoop ? "loop" : "slide",
      perPage: 4,
      perMove: 4,
      gap: "24px",
      arrows: true,
      pagination: false,
      autoplay: isLoop,
      interval: 5000,
      speed: 800,
      pauseOnHover: false,
      breakpoints: {
        991: {
          perPage: 2,
          perMove: 2,
          gap: "16px",
        },
        767: {
          perPage: 1,
          perMove: 1,
          gap: "16px",
        },
      },
    }).mount();
  });
}

function initProductReviewSlider() {
  initFadeAutoplaySlider(".splideProductReviewSlider");
}

/**
 * Blog featured-post hero: konten & gambar cross-fade sebagai satu unit per
 * slide. `rewind: true` supaya arrow tidak pernah disabled (arrow-nya satu
 * instance statis di luar .splide__list, lihat template-parts/content-blog-featured.php).
 */
function initFeaturedPostSlider() {
  const sliders = document.querySelectorAll(".splideFeaturedPostSlider");
  if (!sliders.length) return;

  sliders.forEach((element) => {
    const slideCount = element.querySelectorAll(".splide__slide").length;

    new Splide(element, {
      type: "fade",
      rewind: true,
      perPage: 1,
      perMove: 1,
      arrows: slideCount > 1,
      pagination: false,
      pauseOnHover: false,
    }).mount();
  });
}